export const INSTALL_DISMISS_KEY = 'esf-install-dismiss-until';
export const INSTALL_COOLDOWN = 7 * 24 * 60 * 60 * 1000;
export function deviceInfo(navigator = {}) {
  const ua = navigator.userAgent || '';
  const ios = /iPad|iPhone|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return { platform: ios ? 'ios' : /Android/i.test(ua) ? 'android' : 'other', embedded: /FBAN|FBAV|Instagram|\bwv\b|Line\/|Telegram/i.test(ua) };
}
export function createInstallStore(target, now = () => Date.now()) {
  const listeners = new Set();
  let started = false, media;
  const storedUntil = () => { try { const value = Number(target?.localStorage.getItem(INSTALL_DISMISS_KEY)); return Number.isFinite(value) ? value : 0; } catch { return 0; } };
  const standalone = () => !!(target?.navigator?.standalone || target?.matchMedia?.('(display-mode: standalone)')?.matches || target?.matchMedia?.('(display-mode: fullscreen)')?.matches || target?.matchMedia?.('(display-mode: minimal-ui)')?.matches);
  let state = { ...deviceInfo(target?.navigator), installed: standalone(), prompt: null, busy: false, accepted: false, dismissUntil: storedUntil() };
  const serverState = { platform: 'other', embedded: false, installed: false, prompt: null, busy: false, accepted: false, dismissUntil: 0 };
  const update = values => { state = { ...state, ...values }; listeners.forEach(listener => listener()); };
  const dismiss = () => {
    const until = now() + INSTALL_COOLDOWN;
    try { target?.localStorage.setItem(INSTALL_DISMISS_KEY, String(until)); } catch { /* In-memory suppression still works. */ }
    update({ dismissUntil: until });
  };
  const beforeInstall = event => {
    if (state.installed || state.busy || typeof event.prompt !== 'function') return;
    event.preventDefault(); update({ prompt: event, accepted: false });
  };
  const installed = () => update({ installed: true, prompt: null, accepted: false, busy: false });
  const modeChange = () => { if (standalone()) installed(); };
  const storageChange = event => { if (event.key === INSTALL_DISMISS_KEY) update({ dismissUntil: storedUntil() }); };
  return {
    getSnapshot: () => state,
    getServerSnapshot: () => serverState,
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    start() {
      if (started || !target) return;
      started = true;
      target.addEventListener('beforeinstallprompt', beforeInstall);
      target.addEventListener('appinstalled', installed);
      target.addEventListener('storage', storageChange);
      media = target.matchMedia?.('(display-mode: standalone)');
      if (media?.addEventListener) media.addEventListener('change', modeChange);
      else media?.addListener?.(modeChange);
      if (standalone()) installed();
    },
    stop() {
      if (!started) return;
      target.removeEventListener('beforeinstallprompt', beforeInstall);
      target.removeEventListener('appinstalled', installed);
      target.removeEventListener('storage', storageChange);
      if (media?.removeEventListener) media.removeEventListener('change', modeChange);
      else media?.removeListener?.(modeChange);
      started = false;
    },
    dismiss,
    async install() {
      const event = state.prompt;
      if (!event || state.busy || state.installed) return { outcome: 'unavailable' };
      update({ prompt: null, busy: true });
      try {
        // Keep prompt() in the click gesture; no async work may precede it.
        const result = await event.prompt();
        const choice = result?.outcome ? result : await event.userChoice;
        const outcome = choice?.outcome || 'dismissed';
        if (outcome === 'accepted') update({ accepted: !state.installed });
        else dismiss();
        return { outcome };
      } catch {
        return { outcome: 'error' };
      } finally { update({ busy: false }); }
    },
  };
}
export const installStore = createInstallStore(typeof window !== 'undefined' ? window : null);
export function startInstallCapture() { installStore.start(); }
